import { ChipMetadata, Modal } from '@pmi/dsm-react';
import { useMemo, useState } from 'react';
import { LogContactModel, LogDomainModel, LogModel } from '../models';
import styles from './bulkActionDetailsModal.module.scss';
import ResponsiveTable from '../../../shared/components/ResponsiveTable';
import { SortedColumn } from '../../../shared/components/ResponsiveTable/models';
import {
  getBrowserTimezone,
  toPMIFormatDateWithTime,
} from '../../../shared/helpers';
import basicTableSort from '../../../shared/utils/basicTableSort';

export interface BulkActionDetailsModalProps {
  onCancel: () => void;
  log: LogModel;
  type: string;
}

const parentLogColumns: SortedColumn[] = [
  {
    name: 'performedOn',
    title: `Date/Time (${getBrowserTimezone()})`,
    cell: (cntx) => {
      const formattedDate = cntx.row.original?.performedOn
        ? toPMIFormatDateWithTime(cntx.row.original.performedOn)
        : '';
      return (
        <div className={` fw-normal label`} data-testid="data-status">
          {formattedDate}
        </div>
      );
    },
    width: 10,
    sortingEnabled: false,
  },
  {
    name: 'performedBy',
    title: 'Performed By',
    cell: (cntx) => (
      <span data-exclude-lg>{cntx.row.original?.performedBy?.personName}</span>
    ),
    width: 5,
    sortingEnabled: false,
  },
  {
    name: 'agreementID',
    title: 'Agreement ID',
    cell: (cntx) => (
      <span>
        {cntx.row.original?.data?.agreementProviderId?.toString() ??
          cntx.row.original?.data?.dealId?.toString()}
      </span>
    ),

    width: 5,
    sortingEnabled: false,
  },
  {
    name: 'action',
    title: 'Action',
    cell: ({ row }) => {
      const { data } = row.original;
      const skus: string[] = data?.result?.accountProductSkus ?? [];
      const fallback = data?.agreementProviderId ?? data?.dealId ?? '';
      let skuText = '';
      if (skus.length > 0) {
        skuText = `to ${skus.join(', ')}`;
      } else if (fallback.length > 0) {
        skuText = `to ${fallback}`;
      }
      const inviteCount =
        data?.request?.contactsCount ?? data?.accountUsers?.length ?? 0;
      return (
        <span>
          {`Invited ${inviteCount} user${
            inviteCount > 1 ? 's' : ''
          } ${skuText}`}
        </span>
      );
    },
    width: 15,
    sortingEnabled: false,
  },
];
const invitedEmailListColumns = (columnText: string): SortedColumn[] => [
  {
    name: 'value',
    title: columnText,
    cell: ({ row }) => <span data-exclude-lg>{row.original.value}</span>,
    width: 5,
    sortingEnabled: true,
  },
];

export default function BulkActionDetailsModal({
  onCancel,
  log,
  type,
}: BulkActionDetailsModalProps) {
  const [sortedColumn, setSortedColumn] = useState<SortedColumn>(null);
  const handleSort = (column: SortedColumn) => {
    setSortedColumn(column);
  };
  const accountProductSkus: string[] =
    log?.data?.result?.accountProductSkus ?? [];
  const skuText =
    accountProductSkus.length > 0
      ? accountProductSkus.join(', ')
      : log?.data?.agreementProviderId ?? log?.data?.dealId ?? '';
  const invited = useMemo<{ value: string }[]>(() => {
    // Cosmos-era rows carry the processed result; SQL/V3 `Users.AddUsers` rows are queued, so only the
    // requested contacts (data.accountUsers) / domains (data.request.Domains, PascalCase) are known.
    const result = log?.data?.result;
    let items: { value: string }[] = [];
    if (type === 'domain') {
      const domains =
        result?.domains?.completed ??
        log?.data?.request?.domains ??
        log?.data?.request?.Domains ??
        [];
      items = domains.map((domain: LogDomainModel & { Name?: string }) => ({
        value: domain.name ?? domain.Name,
      }));
    } else {
      const contacts =
        result?.contacts?.completed ?? log?.data?.accountUsers ?? [];
      items = contacts.map((contact: LogContactModel) => ({
        value: contact.email,
      }));
    }
    if (sortedColumn?.direction) {
      const sortOrder = sortedColumn.direction === 'Asc' ? 1 : -1;
      items = basicTableSort(items, 'value', sortOrder);
    }
    return items;
  }, [log, type, sortedColumn]);
  const dataListColumns: SortedColumn[] = useMemo(
    () => invitedEmailListColumns(type === 'email' ? 'Email' : 'Domain'),
    [type],
  );
  return (
    <Modal
      show
      onClose={onCancel}
      variant="input"
      customClassName={styles.logDetailsModal}
      buttons={[
        {
          titleText: 'Close',
          variant: 'primary',
          onClick: onCancel,
        },
      ]}
    >
      <div className={styles.modalContents}>
        <div className="p-2 text-center">
          <h4 className="m-0 p-0 pb-3">Log Detail</h4>
        </div>
        <div className="d-block">
          <div className="d-block mb-4">
            <ResponsiveTable data={[log]} columns={parentLogColumns} />
          </div>
          <div className="d-block">
            <h5 className="d-flex align-items-center align-content-center">
              Invited {type === 'domain' ? 'Domains' : 'Users'}
              <ChipMetadata
                text={`${invited.length} ${
                  type === 'domain' ? 'domain' : 'user'
                }${invited.length > 1 ? 's' : ''}`}
                variant="violet"
                className="ms-3"
              />
            </h5>
            <p>
              {type === 'domain'
                ? 'Domains that have been set up for '
                : 'Users invited to '}
              {skuText}
            </p>
            <div className={styles.logDetailsBlock}>
              <ResponsiveTable
                data={invited}
                columns={dataListColumns}
                onSort={handleSort}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
